<template>
  <article class="documents-review-card">
    <button type="button" class="documents-review-profile" @click="$emit('show-employee', review.employeeId)">
      <img
        v-if="avatarUrl"
        :src="avatarUrl"
        :alt="employeeName"
        class="documents-review-avatar"
      />
      <div v-else class="documents-review-avatar documents-review-avatar--fallback">
        {{ initials }}
      </div>

      <div class="documents-review-profile-copy">
        <span class="documents-review-name">{{ employeeName }}</span>
        <span v-if="review.assessmentName" class="documents-review-assessment">
          {{ review.assessmentName }}
        </span>
      </div>
    </button>

    <div class="documents-review-meta">
      <Tag
        v-if="showPhase1"
        :value="t('wkt.client.document.assessment-review-card.self-appraisal')"
        severity="info"
      />
      <Tag
        v-else-if="showPhase2"
        :value="t('wkt.client.document.assessment-review-card.final-evaluation')"
        severity="warning"
      />
      <span v-if="deadlineLabel" class="documents-review-deadline">{{ deadlineLabel }}</span>
    </div>

    <div class="documents-review-actions">
      <Button
        v-if="canShowPhase1Submission"
        severity="secondary"
        text
        :label="t('wkt.client.document.assessment-review-card.see-self-assessment')"
        @click="$emit('show-phase1', review)"
      />
      <Button
        :label="t('wkt.client.document.assessment-review-card.complete')"
        @click="$emit('open', review)"
      />
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import type { FullAssessmentReviewDTO } from "@wkt/api/documents";
import { useUtils } from "@wkt/composables/useUtils";

const ASSESSMENT_TYPE_PHASE1_ONLY = 1;
const ASSESSMENT_TYPE_PHASE2_ONLY = 2;
const ASSESSMENT_TYPE_PHASE1_AND_PHASE2 = 3;

const props = defineProps<{
  review: FullAssessmentReviewDTO;
}>();

defineEmits<{
  (e: "open", review: FullAssessmentReviewDTO): void;
  (e: "show-phase1", review: FullAssessmentReviewDTO): void;
  (e: "show-employee", employeeId?: number | null): void;
}>();

const { t } = useI18n();
const { getProfileAssetUrl } = useUtils();

const employeeName = computed(() =>
  [props.review.employeeFirstname, props.review.employeeLastname].filter(Boolean).join(" ").trim() || "---"
);

const initials = computed(() => {
  const parts = employeeName.value.split(" ").filter(Boolean);
  return parts.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join("") || "?";
});

const avatarUrl = computed(() =>
  getProfileAssetUrl(props.review.employeeProfilePicture)
);

const showPhase1 = computed(
  () =>
    !props.review.phase1CompletionDate &&
    (props.review.assessmentType === ASSESSMENT_TYPE_PHASE1_ONLY ||
      props.review.assessmentType === ASSESSMENT_TYPE_PHASE1_AND_PHASE2)
);

const showPhase2 = computed(
  () =>
    !props.review.phase2CompletionDate &&
    (props.review.assessmentType === ASSESSMENT_TYPE_PHASE2_ONLY ||
      props.review.assessmentType === ASSESSMENT_TYPE_PHASE1_AND_PHASE2)
);

const canShowPhase1Submission = computed(
  () => Boolean(props.review.phase1CompletionDate) && showPhase2.value
);

function formatDateTime(value?: string | null): string {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

const deadlineLabel = computed(() => {
  const deadline = showPhase1.value ? props.review.phase1DueDate : props.review.phase2DueDate;
  const formattedDeadline = formatDateTime(deadline);
  if (!formattedDeadline) {
    return "";
  }

  return `${t("wkt.client.document.assessment-review-card.deadline")} ${formattedDeadline}`;
});
</script>

<style scoped>
.documents-review-card {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  min-height: 248px;
  border: 1px solid rgba(226, 232, 240, 0.9);
  border-radius: 18px;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
}

.documents-review-profile {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.documents-review-avatar {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  object-fit: cover;
  background: #e2e8f0;
}

.documents-review-avatar--fallback {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #1e293b;
  font-weight: 700;
}

.documents-review-profile-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.documents-review-name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--documents-text, #0f172a);
}

.documents-review-assessment {
  font-size: 0.92rem;
  color: var(--documents-subtle, #475569);
}

.documents-review-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.documents-review-deadline {
  font-size: 0.92rem;
  color: var(--documents-subtle, #64748b);
}

.documents-review-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  margin-top: auto;
}

@media (max-width: 767px) {
  .documents-review-card {
    padding: 16px;
    border-radius: 16px;
    min-height: auto;
  }

  .documents-review-actions {
    justify-content: stretch;
  }

  .documents-review-actions :deep(.p-button) {
    width: 100%;
  }
}
</style>
